import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../redux/store';
import type { EwsDurationKey } from '../../common/constants';

// Dedicated slice for the per-term EWS request/response maps. Kept out of `adviceGoals.global`
// so a streamed per-term update never replaces the whole `adviceGoals` object (which re-renders
// every subscriber). This slice is the single runtime source of truth; the legacy
// `global.ewsRequests/ewsResponses` shape only survives in the persisted document (save/hydrate).

// Single, slice-level rollup of everything ClientJourneyFlow2 needs to gate the stepper and the
// "Continue" CTA — so the journey subscribes to this ONE object instead of the full response maps
// and re-renders only when an aggregate flips, not on every streamed per-term tick. It is written
// by the EWS event-stream handler (see utils/ewsUnifiedEventStream.ts), not derived in the slice.
export interface EwsStepperCtaStatus {
    anyLoading: boolean;
    anyError: boolean;
    // Every term that exists has a response and is neither loading nor errored (true when empty).
    allTermsReady: boolean;
    hasAnyTerm: boolean;
    hasNoEligibleFunds: boolean;
}

// AEX-3603 Continuity of Advice: per-goal continuity flags + prior recommended portfolio. Absent
// goals resolve to the first-time onboarding baseline (all flags true, no prior portfolio).
export interface AdviceContinuityGoal {
    isNewGoal: boolean;
    isNewGoalPriority: boolean;
    isNewInvestmentTenure: boolean;
    currentPortfolioId: string | null;
}

// AEX-3603 Continuity of Advice: the continuity attributes the FE passes to EWS, kept as one object
// so payload formatting can copy them straight onto the request. Top-level `reallocate`/
export interface AdviceContinuityState {
    reallocate: boolean;
    isNewRiskProfile: boolean;
    goals: Record<string, AdviceContinuityGoal>;
}

export interface EwsState {
    requests: Record<string, any>;
    responses: Record<string, any>;
    stepperCtaStatus: EwsStepperCtaStatus;
    // Term buckets marked dirty by the last goal/account/profile/risk change; drives which terms
    // EWS re-runs and gates the journey CTA/step status. Persisted into the saved document under
    // global.affectedEwsTerms (see withReconstructedEwsGlobal) and re-marked on hydrate.
    affectedEwsTerms: EwsDurationKey[];
    // True only while the unified EWS stream is in flight (transient UI flag, not persisted).
    ewsLoading: boolean;
    // AEX-3603 Continuity of Advice attributes merged into the EWS payload at format time.
    adviceContinuity: AdviceContinuityState;
}

const initialStepperCtaStatus: EwsStepperCtaStatus = {
    anyLoading: false,
    anyError: false,
    allTermsReady: true,
    hasAnyTerm: false,
    hasNoEligibleFunds: false
};

// First-time onboarding baseline (also the default for any goal not tracked in `goals`): every
// change flag is true and there is no prior recommended portfolio. Reallocation seeding flips
// these to the continuity values per AEX-3603.
export const ONBOARDING_BASELINE_CONTINUITY_GOAL: AdviceContinuityGoal = {
    isNewGoal: true,
    isNewGoalPriority: true,
    isNewInvestmentTenure: true,
    currentPortfolioId: null
};

// Baseline for an existing goal during reallocation: no changes until the advisor edits it.
const EXISTING_CONTINUITY_GOAL: Omit<AdviceContinuityGoal, 'currentPortfolioId'> = {
    isNewGoal: false,
    isNewGoalPriority: false,
    isNewInvestmentTenure: false
};

const initialAdviceContinuity: AdviceContinuityState = {
    reallocate: false,
    isNewRiskProfile: true,
    goals: {}
};

const initialState: EwsState = {
    requests: {},
    responses: {},
    stepperCtaStatus: initialStepperCtaStatus,
    affectedEwsTerms: [],
    ewsLoading: false,
    adviceContinuity: initialAdviceContinuity
};

// A term is errored when the term itself failed or any of its goal runpipes came back with success: false.
export const isEwsTermErrored = (term: any): boolean =>
    !!term?.error || (term?.response?.body?.runPipeResponses ?? []).some((run: any) => run?.success === false);

// Pure rollup of the per-term response map into the stepper/CTA gating flags. Lives here so the
// stream handler and any other writer compute it identically.
export const computeEwsStepperCtaStatus = (responses: Record<string, any>): EwsStepperCtaStatus => {
    const terms = Object.values(responses ?? {});
    return {
        anyLoading: terms.some((t: any) => t?.loading),
        anyError: terms.some(isEwsTermErrored),
        allTermsReady: terms.every((t: any) => !t?.loading && !isEwsTermErrored(t) && !!t?.response),
        hasAnyTerm: terms.length > 0,
        hasNoEligibleFunds: terms.some((t: any) =>
            t?.response?.body?.runPipeResponses?.some((run: any) => run?.noEligibleFundings)
        )
    };
};

const ewsSlice = createSlice({
    name: 'ews',
    initialState,
    reducers: {
        // Merge ONE term; only that term's reference changes (the flicker-critical action).
        setEwsTerm: (state, action: PayloadAction<{ term: string; request?: any; response?: any }>) => {
            const { term, request, response } = action.payload;
            if (request !== undefined) state.requests[term] = request;
            if (response !== undefined) state.responses[term] = response;
        },
        // Replace whole maps (loading seed, post-stream merge, goal-deletion prune result, hydrate).
        setEwsMaps: (
            state,
            action: PayloadAction<{ requests?: Record<string, any>; responses?: Record<string, any> }>
        ) => {
            if (action.payload.requests !== undefined) state.requests = action.payload.requests;
            if (action.payload.responses !== undefined) state.responses = action.payload.responses;
        },
        // Aggregate stepper/CTA gating flags, set by the EWS stream handler.
        setEwsStepperCtaStatus: (state, action: PayloadAction<EwsStepperCtaStatus>) => {
            state.stepperCtaStatus = action.payload;
        },
        // Term buckets a goal/account/profile/risk change dirtied; EWS re-runs only these.
        setAffectedEwsTerms: (state, action: PayloadAction<EwsDurationKey[]>) => {
            state.affectedEwsTerms = action.payload;
        },
        // Transient flag toggled by the EWS stream handler while a stream is in flight.
        setEwsLoading: (state, action: PayloadAction<boolean>) => {
            state.ewsLoading = action.payload;
        },
        // AEX-3603 Seed continuity for a reallocation (scenarios #2/#3/#9/#12-#15): reallocate=true,
        // risk profile unchanged, and every existing goal tracked with its prior recommended
        // portfolio and no change flags. Advisor edits then flip individual flags via the updates below.
        // A plan never saved yet is first-time onboarding, so its goals seed from the onboarding baseline.
        seedAdviceContinuityForReallocation: (
            state,
            action: PayloadAction<{
                goals: {
                    goalId: string;
                    currentPortfolioId: string | null;
                    goalContinuityObject?: AdviceContinuityGoal | null;
                }[];
                isPlanSavedOnce: boolean;
            }>
        ) => {
            const { goals, isPlanSavedOnce } = action.payload;
            state.adviceContinuity = {
                reallocate: true,
                isNewRiskProfile: false,
                goals: goals.reduce((acc, { goalId, currentPortfolioId, goalContinuityObject }) => {
                    const baseline = isPlanSavedOnce ? EXISTING_CONTINUITY_GOAL : ONBOARDING_BASELINE_CONTINUITY_GOAL;
                    acc[goalId] = {
                        ...baseline,
                        currentPortfolioId: currentPortfolioId ?? null,
                        ...(goalContinuityObject ?? {})
                    };
                    return acc;
                }, {} as Record<string, AdviceContinuityGoal>)
            };
        },
        // AEX-3603 Plan-level attributes (reallocate, isNewRiskProfile) — e.g. #7 Risk Profile change.
        updatePlanContinuity: (
            state,
            action: PayloadAction<Partial<Pick<AdviceContinuityState, 'reallocate' | 'isNewRiskProfile'>>>
        ) => {
            state.adviceContinuity = { ...state.adviceContinuity, ...action.payload };
        },
        // AEX-3603 Goal-level attributes for one goal — e.g. #4 priority, #5 amount, #6 tenure edits.
        // A goal not yet tracked starts from the existing-goal baseline before the changes apply.
        updateGoalContinuity: (
            state,
            action: PayloadAction<{ goalId: string; changes: Partial<AdviceContinuityGoal> }>
        ) => {
            const { goalId, changes } = action.payload;
            const current = state.adviceContinuity.goals[goalId] ?? {
                ...EXISTING_CONTINUITY_GOAL,
                currentPortfolioId: null
            };
            state.adviceContinuity.goals[goalId] = { ...current, ...changes };
        },
        resetAdviceContinuity: (state) => {
            state.adviceContinuity = initialAdviceContinuity;
        },
        resetEws: () => initialState
    }
});

export const {
    setEwsTerm,
    setEwsMaps,
    setEwsStepperCtaStatus,
    setAffectedEwsTerms,
    setEwsLoading,
    seedAdviceContinuityForReallocation,
    updatePlanContinuity,
    updateGoalContinuity,
    resetAdviceContinuity,
    resetEws
} = ewsSlice.actions;
export default ewsSlice.reducer;

export const selectEwsResponses = (state: RootState): Record<string, any> => state.ews?.responses ?? {};
export const selectEwsRequests = (state: RootState): Record<string, any> => state.ews?.requests ?? {};
export const selectEwsTermResponse =
    (term: string) =>
    (state: RootState): any =>
        selectEwsResponses(state)[term];
export const selectEwsStepperCtaStatus = (state: RootState): EwsStepperCtaStatus =>
    state.ews?.stepperCtaStatus ?? initialStepperCtaStatus;
export const selectAffectedEwsTerms = (state: RootState): EwsDurationKey[] => state.ews?.affectedEwsTerms ?? [];
export const selectEwsLoading = (state: RootState): boolean => state.ews?.ewsLoading ?? false;
export const selectAdviceContinuity = (state: RootState): AdviceContinuityState =>
    state.ews?.adviceContinuity ?? initialAdviceContinuity;
